import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { Truck, Shield, Award, Users, CheckCircle2, PawPrint, ArrowRight } from 'lucide-react';
import { siteImages } from '../data/mock';

const WhyChooseUsPage = () => {
  useEffect(() => {
    document.title = 'Why Choose Us | Paw Supplies Group';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <img 
            src={siteImages.aboutBanner}
            alt="Why Choose Us"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-brown/85 via-brand-brown/60 to-brand-brown/30" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-brand-cream mb-2">
                <PawPrint className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Our Difference</span>
              </div>
              <h1 className="text-4xl md:text-5xl text-white font-extrabold mb-3" className-FONT-DISPLAY>
                Why Choose Paw Supplies Group?
              </h1>
              <p className="text-brand-cream max-w-lg">
                We&apos;re committed to providing the best pet accessories and customer experience.
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main Benefits */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Truck, title: 'Swift Delivery', desc: 'Free USPS shipping across the U.S.A. on every order.' },
              { icon: Shield, title: 'Safe Transactions', desc: 'Your payment information is always protected with 256-bit encryption.' },
              { icon: Award, title: 'Ensuring Excellence', desc: 'Every product is hand-picked for comfort, durability and pet-first design.' },
              { icon: Users, title: 'Customer Support', desc: 'Our friendly team is here to help you find the perfect picks for your pet.' },
            ].map((item, i) => (
              <div key={i} className="bg-brand-cream border border-brand-divider rounded-2xl p-6 hover:border-brand-orange/40 transition-all group">
                <div className="w-14 h-14 bg-gradient-to-br from-brand-orange to-brand-orange-dark rounded-xl flex items-center justify-center mb-5 group-hover:shadow-lg transition-shadow">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>{item.title}</h3>
                <p className="text-brand-brown-light text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Additional Features */}
          <div className="bg-brand-cream border border-brand-divider rounded-3xl p-8 md:p-12 mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-extrabold text-brand-brown mb-4" className-FONT-DISPLAY>
                The Paw Supplies Group Difference
              </h2>
              <p className="text-brand-brown-light max-w-2xl mx-auto">
                Here&apos;s what sets us apart from other pet retailers.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Carefully curated pet product selection',
                'Premium quality at fair prices',
                '30-day hassle-free returns',
                'Fast processing and free shipping',
                'Secure payment processing',
                'Detailed product descriptions',
                'Responsive customer support',
                'Easy-to-use website',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange flex-shrink-0" />
                  <span className="text-brand-brown font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-br from-brand-cream to-brand-orange-light/20 border border-brand-divider rounded-3xl p-12">
            <h2 className="text-3xl font-extrabold text-brand-brown mb-4" className-FONT-DISPLAY>
              Ready to Pamper Your Pet?
            </h2>
            <p className="text-brand-brown-light mb-8 max-w-xl mx-auto">
              Browse our curated selection of pet accessories and experience the Paw Supplies Group difference.
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-orange text-white font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange transition-all shadow-lg"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default WhyChooseUsPage;
